import { fabric } from 'fabric';
import { getObjectsByType } from '../canvas';
import { CanvasDataTypes } from './canvas-data-types';

// Translucent white used to shade the photo zone the user is about to fill.
const PHOTO_ZONE_HIGHLIGHT_FILL = 'rgba(255, 255, 255, 0.45)';

/**
 * Removes any photo-zone selection highlight previously added to the canvas.
 *
 * @param canvas - The fabric canvas to clear the highlight from
 */
export const clearPhotoZoneHighlight = (canvas: fabric.Canvas | null | undefined): void => {
  if (!canvas) {
    return;
  }
  canvas
    .getObjects()
    .filter((obj) => obj.data?.isPhotoZoneHighlight)
    .forEach((obj) => canvas.remove(obj));
};

/**
 * Outlines the currently selected (and still empty) photo zone so the user can
 * see where the next image will be placed. The highlight is a non-interactive
 * translucent overlay tagged as a StrokedMargin so it is stripped from saved
 * personalization and generated previews.
 *
 * @param canvas - The fabric canvas containing the photo zones
 * @param selectedZoneId - The id/name of the selected photo zone, or null to clear
 * @param allowOccupied - PRCM-10916: when `true`, also highlights zones that
 * already have content (e.g. drag-over during a drag-to-canvas replace).
 * Defaults to `false` so existing click/selection callers keep the original
 * empty-zone-only behavior.
 */
export const highlightSelectedPhotoZone = (
  canvas: fabric.Canvas | null | undefined,
  selectedZoneId: string | null | undefined,
  allowOccupied = false,
): void => {
  if (!canvas) {
    return;
  }

  clearPhotoZoneHighlight(canvas);

  if (!selectedZoneId) {
    canvas.renderAll();
    return;
  }

  const zone = getObjectsByType(canvas, CanvasDataTypes.PhotoZone).find(
    (photoZone) =>
      (photoZone.name === selectedZoneId || photoZone.data?.id === selectedZoneId) &&
      (allowOccupied || !photoZone.data?.hasContent),
  );

  if (!zone) {
    canvas.renderAll();
    return;
  }

  const highlight = new fabric.Rect({
    left: zone.left,
    top: zone.top,
    width: zone.width,
    height: zone.height,
    angle: zone.angle,
    originX: zone.originX,
    originY: zone.originY,
    fill: PHOTO_ZONE_HIGHLIGHT_FILL,
    strokeWidth: 0,
    selectable: false,
    evented: false,
    hasControls: false,
    hasBorders: false,
    data: { type: CanvasDataTypes.StrokedMargin, isPhotoZoneHighlight: true },
  });

  canvas.add(highlight);
  highlight.bringToFront();
  canvas.renderAll();
};
